import { useEffect, useState } from "react";
import Swal from "sweetalert2";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import Pagination from "@mui/material/Pagination";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import ClientService from "services/client.service";
import ClientCard from "./components/ClientCard";
import CreateEditClientModal from "./components/CreateEditClientModal";
import { useMaterialUIController } from "context";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [openModal, setOpenModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const itemsPerPage = isMobile ? 10 : 25;
  const [page, setPage] = useState(1);

  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;

  // 🔄 carregar clientes
  const loadClients = async () => {
    setLoading(true);
    try {
      const response = await ClientService.getClients();
      setClients(response);
      setFilteredClients(response);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro ao carregar clientes",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  // 🔍 Filtro de busca (Nome, Email, Telefone)
  useEffect(() => {
    const lowerTerm = searchTerm.toLowerCase();
    const filtered = clients.filter((client) => 
      (client.name && client.name.toLowerCase().includes(lowerTerm)) ||
      (client.email && client.email.toLowerCase().includes(lowerTerm)) ||
      (client.phone && client.phone.includes(lowerTerm))
    );
    setFilteredClients(filtered);
    setPage(1); // Volta para a primeira página ao filtrar
  }, [searchTerm, clients]);

  // Paginação
  const paginatedClients = filteredClients.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );
  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // 🗑️ excluir cliente
  const handleDeleteClient = async (client) => {
    const result = await Swal.fire({
      title: "Excluir cliente?",
      text: "Essa ação não pode ser desfeita",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Excluir",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      await ClientService.deleteClient(client.id);

      Swal.fire({
        icon: "success",
        title: "Cliente removido",
        timer: 1200,
        showConfirmButton: false,
      });

        setClients((prev) => prev.filter((c) => c.id !== client.id));
        setFilteredClients((prev) => prev.filter((c) => c.id !== client.id));
        
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro ao excluir cliente",
      });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox p={{ xs: 0.5, md: 3 }}>
        {/* HEADER */}
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <MDTypography variant="h4">Clientes</MDTypography>

          <MDButton
            variant="gradient"
            color={sidenavColor}
            onClick={() => {
              setSelectedClient(null);
              setOpenModal(true);
            }}
          >
            Novo Cliente
          </MDButton>
        </MDBox>

        <MDBox mb={3}>
          <MDInput
            label="Buscar por nome, email ou telefone..."
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              "& .MuiInputBase-input": { color: darkMode ? "#fff" : "inherit" },
              "& .MuiInputLabel-root": { color: darkMode ? "#fff" : "inherit" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: darkMode ? "rgba(255,255,255,0.3)" : "inherit" },
              "& .MuiSvgIcon-root": { color: darkMode ? "#fff" : "inherit" },
            }}
          />
        </MDBox>

        {/* LOADING */}
        {loading && <MDTypography>Carregando...</MDTypography>}

        {/* EMPTY */}
        {!loading && filteredClients.length === 0 && (
          <MDTypography>Nenhum cliente cadastrado</MDTypography>
        )}

        {/* GRID */}
        <MDBox
          display="grid"
          gridTemplateColumns="repeat(auto-fill, minmax(280px, 1fr))"
          gap={2}
        >
          {paginatedClients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onEdit={(client) => {
                setSelectedClient(client);
                setOpenModal(true);
              }}
              onDelete={handleDeleteClient}
            />
          ))}
        </MDBox>

        {/* PAGINAÇÃO */}
        {filteredClients.length > itemsPerPage && (
          <MDBox display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color={sidenavColor}
              sx={{
                "& .MuiPaginationItem-root": {
                  color: darkMode ? "#fff" : "inherit",
                  borderColor: darkMode ? "rgba(255, 255, 255, 0.3)" : "inherit",
                },
              }}
            />
          </MDBox>
        )}
      </MDBox>

      {/* MODAL */}
      <CreateEditClientModal
        open={openModal}
        client={selectedClient}
        onClose={() => setOpenModal(false)}
        onSuccess={() => {
          setOpenModal(false);
          loadClients();
        }}
      />
    </DashboardLayout>
  );
}
